package dev.lingvo.site.pages.app.projects.slug

import dev.lingvo.shared.ActivityDto
import dev.lingvo.shared.BillingOverviewDto
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.state.ProjectActionKey
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectRouteContext
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.actionKey
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import dev.lingvo.site.state.requestWorkspaceResource
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope

/** Activity 13 is unreachable from this page; the row above the fold says so. */
internal const val ACTIVITY_ROW_CAP = 12

internal data class ProjectOverviewData(
    val project: ProjectDetailDto,
    val activity: List<ActivityDto>,
    val billing: BillingOverviewDto?,
    val actionKey: ProjectActionKey,
)

internal data class ProjectOverviewTransport(
    val activity: suspend (String, Int) -> List<ActivityDto>,
    val billing: suspend (String) -> BillingOverviewDto,
)

private val apiProjectOverviewTransport = ProjectOverviewTransport(
    activity = ApiClient::listProjectActivity,
    billing = ApiClient::workspaceBilling,
)

internal suspend fun loadProjectOverview(
    slug: String,
    refreshProject: Boolean,
    directoryScope: CoroutineScope,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(slug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    transport: ProjectOverviewTransport = apiProjectOverviewTransport,
): ProjectOverviewData {
    val entryRoute = currentRoute()?.takeIf { it.project.slug == slug }
        ?: throw CancellationException("The live project route retired before its overview could start.")
    val key = entryRoute.actionKey()
    val memberships = currentMemberships()
    if (refreshProject) {
        requestProjectResource(
            key = key,
            expectedSlug = slug,
            memberships = memberships,
            currentRoute = currentRoute,
            currentMemberships = currentMemberships,
        ) {
            ProjectRouteContext.refresh(slug, directoryScope)
        }
    }
    val route = currentRoute()?.takeIf { it.actionKey() == key }
        ?: throw CancellationException("The live project route retired before its overview could load.")
    val project = route.project
    val activity = requestProjectResource(
        key = key,
        expectedSlug = slug,
        memberships = memberships,
        currentRoute = currentRoute,
        currentMemberships = currentMemberships,
    ) { exactRoute ->
        transport.activity(exactRoute.project.slug, ACTIVITY_ROW_CAP)
    }.sortedByDescending(ActivityDto::createdAt)
    // Only when this person can spend it, and only the org owning this project — a non-admin has
    // no pre-translate button to annotate, and the endpoint refuses them anyway.
    val authority = ProjectAuthority.resolve(project, memberships)
    val billing = if (authority.canEditStructure) {
        // A missing balance is a smaller harm than a project overview that will not load because
        // a billing endpoint is slow — the same reasoning `ReadinessBand` follows with its own
        // state, degrading independently.
        try {
            requestProjectResource(
                key = key,
                expectedSlug = slug,
                memberships = memberships,
                currentRoute = currentRoute,
                currentMemberships = currentMemberships,
            ) { exactRoute ->
                requestWorkspaceResource(
                    context = exactRoute.workspace,
                    privilege = WorkspacePrivilege.BILLING_READ,
                    currentContext = {
                        currentRoute()
                            ?.takeIf { it.actionKey() == key }
                            ?.workspace
                    },
                    transport = transport.billing,
                )
            }
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (_: Throwable) {
            null
        }
    } else {
        null
    }
    return ProjectOverviewData(
        project = project,
        activity = activity,
        billing = billing,
        actionKey = key,
    )
}
